/**
 * Wildlife as statistics: no animal is an entity. Each social cell carries a
 * head count per species, moved daily toward a carrying capacity read from the
 * same habitat summary the settlers use — so a Wither that empties tomorrow's
 * forage empties tomorrow's herds by the same arithmetic, and no second
 * ecology drifts out of sync with the first.
 */

export type WildlifeSpeciesId =
  | 'grazer' | 'browser' | 'small_game' | 'predator' | 'scavenger' | 'fish';

/**
 * The fixed order every per-species loop walks. Trophic species come last so
 * a predator's K reads the prey the same day settled; iteration order is
 * determinism, so nothing may loop over species any other way.
 */
export const WILDLIFE_SPECIES: readonly WildlifeSpeciesId[] =
  ['grazer', 'browser', 'small_game', 'fish', 'predator', 'scavenger'];

/** One species' standing in one social cell, as of `updatedAt` world-seconds. */
export interface WildlifePopulation {
  species: WildlifeSpeciesId;
  sx: number;
  sz: number;
  population: number;
  carryingCapacity: number;
  updatedAt: number;
}

export interface WildlifeArchetype {
  /** Logistic growth per day toward carrying capacity. */
  growthPerDay: number;
  /** Head per cell where the weighted habitat reads a full 1.0 — and the hard ceiling for the trophic species. */
  headAtBest: number;
  /** How the cell's habitat weighs into K. The trophic species leave this zero: they are carried by prey and carrion, not by grass. */
  habitatWeight: { forage: number; woody: number; water: number };
  /** Lives on the water fraction, so K is never scaled down by land fraction. */
  aquatic?: boolean;
  /** How readily a hunting party takes them, 0..1. */
  huntability: number;
  /** Food units one head yields when taken. */
  foodPerHead: number;
  /** What it eats (or scavenges). Empty at the base of the pyramid. */
  prey: readonly WildlifeSpeciesId[];
  /**
   * The head count that makes a migration of this kind a GREAT one — the
   * moment the world is allowed to say "the herds moved on" and a band is
   * allowed to remember it.
   *
   * It has to be per species and it has to be measured. Salience used to be
   * `0.08 + moved / 80` against a 0.7 gate, i.e. 49.6 head for everything
   * alive: over two god-free years exactly 14 of 28,101 migrations could be
   * spoken about and every one was small game, because a predator cell holds
   * single figures by definition. The bar measured how small the animal was.
   *
   * Neither obvious denominator works. `cap` is what migration is TRIGGERED
   * by collapsing, so `pop / cap` is far above 1 at exactly the moment the
   * event fires — normalising by it made 47% of all migrations great and
   * grazers 100%. `headAtBest` is stable but this island realises 13% of it
   * for grazers against 41% for predators, so no one coefficient over it
   * serves both; the habitat factor is real and per species.
   *
   * So: a high quantile of what that kind actually moves here, measured by
   * `npx tsx tools/migration.ts` and stable — 2 and 6 god-free years agree to
   * a tenth of a head on every species.
   *
   * **Which quantile is not the same for every kind, and that is deliberate.**
   * Small game migrates 11,559 times in two god-free years and grazers 15 —
   * three orders of magnitude apart — so one percentile cannot serve both. At
   * p99 a grazer's own ceiling IS its p99 and the kind can never once qualify;
   * at p90 small game alone would produce hundreds of great migrations a year
   * and drown every other sentence the world has (`wildlife_migrated` is
   * already 99.9% of the event stream). So the abundant kinds take p99 and the
   * rare movers p90: every kind can produce one, and the island as a whole
   * says it about 1% of the time. Re-measure if the archetypes, the habitat
   * model or the world change.
   */
  greatMove: number;
}

export const WILDLIFE_ARCHETYPES: Record<WildlifeSpeciesId, WildlifeArchetype> = {
  grazer:     { growthPerDay: 0.06,  headAtBest: 80,  habitatWeight: { forage: 0.9,  woody: 0,    water: 0.1 },
                huntability: 0.65, foodPerHead: 4,    prey: [], greatMove: 6.5 },
  browser:    { growthPerDay: 0.045, headAtBest: 45,  habitatWeight: { forage: 0.15, woody: 0.85, water: 0 },
                huntability: 0.5,  foodPerHead: 3.5,  prey: [], greatMove: 4.2 },
  small_game: { growthPerDay: 0.08,  headAtBest: 220, habitatWeight: { forage: 0.5,  woody: 0.5,  water: 0 },
                huntability: 0.8,  foodPerHead: 0.6,  prey: [], greatMove: 39.1 },
  // fish never migrate here — aquatic cells have no stocked land neighbours to
  // walk to, so this has never been exercised. Set from the median ratio the
  // five land species show between greatMove and headAtBest (0.178), so that
  // if fish ever do move they are on the same footing rather than silent.
  fish:       { growthPerDay: 0.07,  headAtBest: 150, habitatWeight: { forage: 0,    woody: 0,    water: 1 },
                aquatic: true, huntability: 0.7, foodPerHead: 0.8, prey: [], greatMove: 26.7 },
  predator:   { growthPerDay: 0.03,  headAtBest: 8,   habitatWeight: { forage: 0,    woody: 0,    water: 0 },
                huntability: 0.2,  foodPerHead: 2.5,  prey: ['grazer', 'browser', 'small_game'], greatMove: 2.35 },
  scavenger:  { growthPerDay: 0.05,  headAtBest: 20,  habitatWeight: { forage: 0,    woody: 0,    water: 0 },
                huntability: 0.35, foodPerHead: 0.8,  prey: ['grazer', 'browser', 'small_game'], greatMove: 5.15 },
};

/**
 * The species that eat the range — the ones whose K reads the sere's stage
 * shares, the season, and the cell's range condition. Predators are carried
 * by prey and scavengers by death, and fish live on the water fraction; none
 * of those three strips a hillside.
 */
export const HERBIVORES: readonly WildlifeSpeciesId[] = ['grazer', 'browser', 'small_game'];

/**
 * The mutable per-cell ledger the wildlife system keeps. Internal to
 * src/sim/wildlife: populations.ts writes it daily, migration.ts moves heads
 * between ledgers. Nothing outside reads this shape — the public view is
 * WildlifePopulation snapshots.
 */
export interface WildlifeCellState {
  sx: number;
  sz: number;
  pop: Record<WildlifeSpeciesId, number>;
  cap: Record<WildlifeSpeciesId, number>;
  /** Yesterday's K, for the "habitat declined" migration trigger. */
  prevCap: Record<WildlifeSpeciesId, number>;
  /** Carrion pool in head-units — deaths land here, decay drains it, the scavenger's K reads it. */
  carrion: number;
  /**
   * Range condition, 0..1 — the slow variable (§14's "trajectory"). 1 is
   * pristine. Overstock strips it in days and it comes back over years, so a
   * boom leaves a scar the herbivore K reads long after the boom is over
   * (law 19: protecting something changes it). At equilibrium nothing is
   * overstocked and it sits at 1, so a world nobody has pushed is unchanged.
   */
  range: number;
  /**
   * The world-second a species last died out of this cell, present only while
   * it is still absent — what `wildlife_returned` reads to say how long. A
   * species with an entry here has pop 0; a species without one may too (it
   * was never established, or the ledger predates the field).
   */
  gone: Partial<Record<WildlifeSpeciesId, number>>;
  /**
   * A HAND ON THE HERD — the Beasts direction's one ACT (`ROADMAP` Beasts
   * lane, 2026-08-17). Signed, per kind, and only present while non-zero: a
   * god's Strengthen aimed at a herd that was already inclined to move, to
   * increase, or to hunt writes it (`Simulation.act` → `WildlifeSystem.lean`),
   * `daily` decays it on a measured half-life, and exactly three readers
   * consume it — `migrationPass` (the bar and the moved fraction),
   * `daily`'s logistic growth, and `daily`'s predation intake — each only
   * while the process it names is actually happening. It carries no god
   * (law 10): the ledger records that something is leaning on this herd and
   * never whose hand. Optional in the record so a file written before it
   * decodes to the same world it always did.
   */
  press?: HerdPresses;
  updatedAt: number;
}

/** The three things a herd can be doing that a hand can lean on. */
export type BeastProcess = 'movement' | 'increase' | 'hunt';

/**
 * A lean on one herd, per process: how much, signed in whole presses, and how
 * long it lasts. Keyed by the process it was laid on — a hand on a herd's
 * increase is not a hand on its drift, and the first cut of this kept one
 * number per herd and found out why: a herd pressed to fill its ground filled
 * it, read its emptied neighbours as better, and walked out on the same
 * press. Law 13 in the ledger: the press names the process.
 */
export interface HerdPress {
  v: number;
  /** Half-life in world-days, set at the deposit (a patient god's is longer). */
  half: number;
}
export type HerdPresses = Partial<Record<WildlifeSpeciesId, Partial<Record<BeastProcess, HerdPress>>>>;

/**
 * The per-cell snapshot record this system writes: the canonical
 * `WildlifeCellSnapshot` plus the two fields §14's trajectory work added,
 * both OPTIONAL so a file written before them decodes to the same world it
 * always did (range 1, nothing gone) — the `chronicle?` / `earth?` pattern,
 * no `SNAPSHOT_VERSION` bump, no prehistory rebuild. Kept here rather than in
 * `persistence/snapshot.ts` only because that file is not this lane's;
 * folding these two lines into `WildlifeCellSnapshot` there is the intended
 * end state and changes nothing on disk.
 */
export interface WildlifeCellRecord {
  sx: number;
  sz: number;
  pop: Record<WildlifeSpeciesId, number>;
  cap: Record<WildlifeSpeciesId, number>;
  prevCap: Record<WildlifeSpeciesId, number>;
  carrion: number;
  updatedAt: number;
  range?: number;
  gone?: Partial<Record<WildlifeSpeciesId, number>>;
  press?: HerdPresses;
}

/**
 * A cell where a herd is visibly out of equilibrium right now — the world's
 * answer to "where is something going somewhere". `gathering`: the ground
 * carries more than stands on it, so the herd is growing or arriving (a
 * burn's grass years). `thinning`: more stand than the ground carries, so
 * they are dying back or leaving. `stripped`: the range itself is worn.
 * Read-only and stateless (law 9): a list computed from the ledger.
 */
export interface Stirring {
  sx: number;
  sz: number;
  /** World position of the cell's centre. */
  x: number;
  z: number;
  species: WildlifeSpeciesId;
  kind: 'gathering' | 'thinning' | 'stripped';
  /** 0..1 — how far from equilibrium, weighted by how big a herd it concerns. */
  strength: number;
  /** Head standing there today. */
  head: number;
}

/** One cell's standing for one species, for a card to phrase. */
export interface CellView {
  head: number;
  cap: number;
  range: number;
  trend: 'growing' | 'declining' | 'steady';
  /** World-second the species died out here, if it is currently absent and the ledger knows when. */
  absentSince?: number;
}

/**
 * The terms `WildlifeSystem.capacity` multiplies, for one cell and kind —
 * what the Beast read model turns into *why the ground carries what it does*.
 * Every field is exactly what the daily step reads; nothing is re-derived.
 */
export interface CapacityTerms {
  forage: number;
  woody: number;
  water: number;
  landFraction: number;
  instability: number;
  /** The sere's stage shares over the cell (climax is the remainder). */
  shares: { bare: number; grass: number; shrub: number; young: number };
  /** The season's cut on the forage right now, 0..DRY_CUT — zero by water. */
  cure: number;
  range: number;
  /** What the range condition does to a herbivore's K; 1 for the others. */
  rangeFactor: number;
  /** Prey head standing on the cell (for the kinds that eat prey). */
  prey: number;
  carrion: number;
  cap: number;
  prevCap: number;
}

/** A fresh all-zero per-species record, in the canonical key order. */
export const zeroCounts = (): Record<WildlifeSpeciesId, number> =>
  ({ grazer: 0, browser: 0, small_game: 0, fish: 0, predator: 0, scavenger: 0 });
